/* eslint-disable no-console */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Button, Divider, Flex} from 'antd';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import PeerBenchmarkingBg from '../../../../assets/skeleton/PeerBenchmarkingBg.png';
import BlockAccessPage from '../../../../components/BlockAccess/BlockAccessPage';
import Loader from '../../../../components/Loader/Loader';
import PageContent from '../../../../components/PageContent/PageContent';
import {useGlobalYear} from '../../../../hooks/useGlobalYear';
import Year from '../../../../shared/Year';
import useCompanyStore, {
  accessModule,
  type IUserPeerCompany
} from '../../../../store/companyStore/companyDetailsStore';
// import {yearSelect} from '../../../../utils/FormUtils/yearSelect';
import {shouldBlockModuleAccess} from '../../../../utils/SubscriptionHelper';

import AddPeerCompaniesModal, {
  type PeerCompanyNameOption
} from './AddPeerCompaniesModal';
import PeerBenchMarkingContent from './PeerBenchMarkingContent';

const PEER_CONTENT_LOAD_MS = 500;

const PeerBenchmarking: React.FC = () => {
  const {selectedYear, setSelectedYear} = useGlobalYear();
  const [isPeerContentReady, setIsPeerContentReady] = useState(false);
  const [showAddPeerModal, setShowAddPeerModal] = useState(false);
  const [peerGroupName, setPeerGroupName] = useState<string | null>(null);
  const [peerCompanyOptions, setPeerCompanyOptions] = useState<
    PeerCompanyNameOption[]
  >([]);
  const [requestedPeerIds, setRequestedPeerIds] = useState<number[] | null>(
    null
  );
  const [shownPeerNames, setShownPeerNames] = useState<string[] | null>(null);
  const userPeerCompanies = useCompanyStore((state) => state.userPeerCompanies);
  const setUserPeerCompanies = useCompanyStore(
    (state) => state.setUserPeerCompanies
  );

  const peerAccessBlocked = shouldBlockModuleAccess(
    accessModule.Reporting_And_ESG,
    accessModule.peer_benchmarking
  );

  const peerContentYearKey = selectedYear;
  const [readyYearKey, setReadyYearKey] = useState(peerContentYearKey);

  if (peerContentYearKey !== readyYearKey) {
    setReadyYearKey(peerContentYearKey);
    setIsPeerContentReady(false);
  }

  const handlePeerCompanies = useCallback(
    (options: PeerCompanyNameOption[]) => {
      setPeerCompanyOptions(options);
    },
    []
  );
  const applyPeerCompanies = useCallback(
    (companies: IUserPeerCompany[]) => {
      const peerIds = companies
        .map(
          (company) =>
            peerCompanyOptions.find(
              (option) => option.value === company.companyName
            )?.entityId
        )
        .filter((id): id is number => id !== undefined);

      // Nothing selected: keep the current benchmark until a company is picked.
      if (!peerIds.length) {
        return;
      }

      setUserPeerCompanies(companies);
      setRequestedPeerIds((current) =>
        current &&
        current.length === peerIds.length &&
        current.every((id, index) => id === peerIds[index])
          ? current
          : peerIds
      );
    },
    [peerCompanyOptions, setUserPeerCompanies]
  );
  const modalInitialCompanies = useMemo(() => {
    if (!shownPeerNames) {
      return userPeerCompanies;
    }

    return shownPeerNames
      .filter((name) =>
        peerCompanyOptions.some((option) => option.value === name)
      )
      .map((name) => ({
        companyName: name,
        companyWebsite:
          peerCompanyOptions.find((option) => option.value === name)?.website ??
          ''
      }));
  }, [peerCompanyOptions, shownPeerNames, userPeerCompanies]);

  useEffect(() => {
    if (peerAccessBlocked) {
      setIsPeerContentReady(false);

      return;
    }
    if (!peerContentYearKey) {
      setIsPeerContentReady(false);

      return;
    }
    setIsPeerContentReady(false);
    const timer = window.setTimeout(() => {
      setIsPeerContentReady(true);
    }, PEER_CONTENT_LOAD_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [peerAccessBlocked, peerContentYearKey]);

  return (
    <PageContent>
      {selectedYear && (
        <Flex vertical={false} align="center" justify="space-between">
          <Flex align="center" justify="space-around" gap={5}>
            <div
              style={{
                font: 'normal normal 700 16px Montserrat',
                letterSpacing: '0.56px',
                color: '#282A3A'
              }}
            >
              Peer Benchmarking
            </div>
            <Flex
              style={{
                padding: '6px 12px',
                backgroundColor: '#F3F4F8',
                borderRadius: '100px',
                font: 'normal normal 500 14px Montserrat',
                height: '32px'
              }}
              gap={5}
            >
              <InfoCircleOutlined />
              Based on BRSR Framework
            </Flex>
            {/* The label stays visible; the name fills in once the API loads. */}
            <div
              style={{
                font: 'normal normal 700 16px Montserrat',
                letterSpacing: '0.56px',
                color: '#282A3A'
              }}
            >
              Industry: {peerGroupName ?? ''}
            </div>
          </Flex>
          {peerAccessBlocked ? null : (
            <Flex align="center" gap={12}>
              <Button
                onClick={() => {
                  setShowAddPeerModal(true);
                }}
              >
                Add your Peer companies
              </Button>
              <Year
                setSelectedYear={setSelectedYear}
                isESG
                colSpan={24}
                noBottomMargin
              />
            </Flex>
          )}
        </Flex>
      )}
      <Divider
        orientationMargin="0"
        className="divider"
        style={{margin: '12px'}}
      />
      {peerAccessBlocked ? (
        <div style={{height: 900}}>
          <BlockAccessPage backgroundImage={PeerBenchmarkingBg} />
        </div>
      ) : isPeerContentReady ? (
        <PeerBenchMarkingContent
          selectedYear={peerContentYearKey}
          onPeerGroupName={setPeerGroupName}
          onPeerCompanies={handlePeerCompanies}
          onShownPeerNames={setShownPeerNames}
          requestedPeerIds={requestedPeerIds}
        />
      ) : (
        <div style={{minHeight: 480}}>
          <Loader isHome={true} />
        </div>
      )}
      <AddPeerCompaniesModal
        open={showAddPeerModal}
        initialCompanies={modalInitialCompanies}
        companies={peerCompanyOptions}
        onClose={() => {
          setShowAddPeerModal(false);
        }}
        onSubmit={(companies) => {
          applyPeerCompanies(companies);
          setShowAddPeerModal(false);
        }}
      />
    </PageContent>
  );
};

export default PeerBenchmarking;
